import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock } from '../../components/Spinner'
import type { Page } from '../../crm/client/pager'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { usePagedList } from '../../library/usePagedList'
import { useToast } from '../../library/useToast'
import { MediaViewer } from '../../library/vault/MediaViewer'
import type { VaultItem } from '../../library/vault/vaultData'
import { Composer } from '../home/Composer'
import type { When } from '../home/homeData'
import { createOfApi } from '../ofApi'
import { AutoPublishPanel, LaterList } from './LaterView'
import type { QueueActions } from './QueueItem'
import { Calendar, DayPane, QueueList } from './QueueViews'
import {
  addDays,
  browserTimeZone,
  canGoBack,
  countersQuery,
  countersRange,
  dayInZone,
  dayQuery,
  firstOf,
  listQuery,
  listRange,
  monthOf,
  sameMonth,
  SCHEDULE_TYPES,
  shiftMonth,
  todayIn,
  type Month,
  type ScheduleType,
  type Ymd,
} from './queueDates'
import { applyQueueEdit, editBody, LATER_REFERER, laterFetcher, scheduleOn, toCounters, toScheduleItem, type DayCount, type LaterKind, type ScheduleItem } from './queueData'
import { dayLabel } from './queueFormat'
import '../../library/library.css'
import '../ofPages.css'
import '../home/home.css'
import './queue.css'

const REFERER = 'https://onlyfans.com/my/queue'
const LAYOUT_KEY = 'ofq.layout'

type Layout = 'calendar' | 'list'
type View = 'queue' | LaterKind
type Payload = { list?: Record<string, unknown>[]; hasMore?: boolean }

interface Props {
  /** The page is keyed by it (AccountCrm): another account is a fresh page. */
  accountId: string
  canWrite: boolean
  /** The phone's bottom tab bar (the CRM pages). */
  mobileNav?: ReactNode
  /** IANA zone OnlyFans files days by; the browser's by default, as on OnlyFans. */
  timeZone?: string
}

function readLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'calendar'
  } catch {
    return 'calendar'
  }
}

const itemId = (i: ScheduleItem) => i.id
const nothing = async (): Promise<Page<ScheduleItem>> => ({ list: [], hasMore: false })

/** Queue: the Account's scheduled and saved-for-later OnlyFans posts (OnlyFans /my/queue, SPEC §2). */
export function QueuePage({ accountId, canWrite, mobileNav, timeZone = browserTimeZone() }: Props) {
  const { t, i18n } = useTranslation('ofQueue')
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const { say, node: toast } = useToast()
  const mobile = useIsNarrow()
  const today = todayIn(timeZone)

  const [view, setView] = useState<View>('queue')
  const [month, setMonth] = useState<Month>(() => monthOf(today))
  const [day, setDay] = useState<Ymd>(today)
  const [sheet, setSheet] = useState(false)
  const [layout, setLayoutState] = useState<Layout>(readLayout)
  const [types, setTypes] = useState<ScheduleType[]>([])
  const [countsToken, setCountsToken] = useState(0)
  const [pinned, setPinned] = useState<Record<number, boolean>>({})
  const [viewer, setViewer] = useState<{ items: VaultItem[]; index: number } | null>(null)
  /** The new-post composer (Home's), open with this schedule preset. */
  const [compose, setCompose] = useState<When | null>(null)
  const [listToken, setListToken] = useState(0)
  const [laterToken, setLaterToken] = useState(0)
  const inQueue = view === 'queue'

  const setLayout = (next: Layout) => {
    setLayoutState(next)
    try {
      localStorage.setItem(LAYOUT_KEY, next)
    } catch {
      // Private mode: the choice lasts this visit.
    }
  }

  // ── Calendar counters ────────────────────────────────────────────────────
  const [counts, setCounts] = useState<Record<Ymd, DayCount> | null>(null)
  useEffect(() => {
    if (!inQueue || layout !== 'calendar') return
    let live = true
    of.get<Record<string, unknown>>('schedules/counters', countersQuery(countersRange(month, today), types, timeZone), REFERER)
      .then((p) => live && setCounts(toCounters(p)))
      .catch(() => live && setCounts((c) => c ?? {}))
    return () => {
      live = false
    }
  }, [of, inQueue, layout, month, today, types, timeZone, countsToken])

  // ── The day pane (OnlyFans answers the whole day at once) ────────────────
  // A busy day is seconds and half a megabyte, so it never holds up the
  // calendar, phones only ask once the day sheet opens, and a day seen on
  // this visit comes back from memory.
  const [dayItems, setDayItems] = useState<ScheduleItem[]>([])
  const [dayLoading, setDayLoading] = useState(false)
  const [dayFailed, setDayFailed] = useState(false)
  const [dayToken, setDayToken] = useState(0)
  const dayCache = useRef(new Map<string, ScheduleItem[]>())
  const showsDay = inQueue && (!mobile || sheet)
  useEffect(() => {
    if (!showsDay) return
    const key = `${day} ${types.join()}`
    const seen = dayCache.current.get(key)
    setDayFailed(false)
    if (seen) {
      setDayItems(seen)
      setDayLoading(false)
      return
    }
    let live = true
    setDayItems([])
    setDayLoading(true)
    of.get<Payload>('schedules', dayQuery(day, types, timeZone), REFERER)
      .then((p) => {
        const items = (p?.list ?? []).map(toScheduleItem)
        dayCache.current.set(key, items)
        if (live) setDayItems(items)
      })
      .catch(() => live && setDayFailed(true))
      .finally(() => live && setDayLoading(false))
    return () => {
      live = false
    }
  }, [of, showsDay, day, types, timeZone, dayToken])
  const reloadDay = () => {
    dayCache.current.clear()
    setDayToken((n) => n + 1)
  }

  // ── List layout and saved for later (offset paging) ──────────────────────
  // A new fetcher starts the list over: the tokens bump after a post is added.
  const fetchList = useMemo(() => {
    void listToken
    return async (offset: number): Promise<Page<ScheduleItem>> => {
      const p = await of.get<Payload>('schedules', listQuery(listRange(month, today), types, timeZone, offset), REFERER)
      return { list: (p?.list ?? []).map(toScheduleItem), hasMore: p?.hasMore }
    }
  }, [of, month, today, types, timeZone, listToken])
  const list = usePagedList(inQueue && layout === 'list' ? fetchList : nothing, itemId)
  const fetchLater = useMemo(() => {
    void laterToken
    return view === 'queue' ? nothing : laterFetcher(of, view)
  }, [of, view, laterToken])
  const later = usePagedList(fetchLater, itemId)

  // One centred spinner until the first screenful has arrived; later loads stay inline.
  const [booted, setBooted] = useState(false)
  const pageIn = (l: { loading: boolean; failed: boolean; hasMore: boolean; items: unknown[] }) => !l.loading && (l.failed || !l.hasMore || l.items.length > 0)
  const firstReady = !inQueue ? pageIn(later) : layout === 'list' ? pageIn(list) : counts !== null
  useEffect(() => {
    if (firstReady) setBooted(true)
  }, [firstReady])

  // ── Navigation ───────────────────────────────────────────────────────────
  const goMonth = (n: number) => {
    const next = shiftMonth(month, n)
    setMonth(next)
    const first = firstOf(next)
    setDay(first > today ? first : today)
  }
  const pickDay = (d: Ymd) => {
    setDay(d)
    if (!sameMonth(monthOf(d), month)) setMonth(monthOf(d))
    if (mobile) setSheet(true)
  }
  const toggleType = (ty: ScheduleType) => setTypes((all) => (all.includes(ty) ? all.filter((x) => x !== ty) : [...all, ty]))

  // ── Writes (SPEC §4) ─────────────────────────────────────────────────────
  const referer = view === 'queue' ? REFERER : LATER_REFERER[view]
  const changeItem = (id: number, change: (i: ScheduleItem) => ScheduleItem) => {
    const apply = (all: ScheduleItem[]) => all.map((i) => (i.id === id ? change(i) : i))
    for (const [key, items] of dayCache.current) dayCache.current.set(key, apply(items))
    setDayItems(apply)
    list.setItems(apply)
    later.setItems(apply)
  }
  const dropItem = (id: number) => {
    const keep = (all: ScheduleItem[]) => all.filter((i) => i.id !== id)
    for (const [key, items] of dayCache.current) dayCache.current.set(key, keep(items))
    setDayItems(keep)
    list.setItems(keep)
    later.setItems(keep)
    setCountsToken((n) => n + 1)
  }
  const tell = (e: unknown): never => {
    say(e instanceof Error && e.message ? e.message : t('error'))
    throw e
  }
  const isPinned = (item: ScheduleItem) => (item.post ? (pinned[item.post.id] ?? item.post.isPinned) : false)

  const actions: QueueActions = {
    canWrite,
    accountId,
    of,
    timeZone,
    isPinned,
    pin: async (item) => {
      const post = item.post!
      const next = !isPinned(item)
      await of.send('POST', `posts/${post.id}/pin`, referer, undefined, { format: 'status' }).catch(tell)
      setPinned((all) => ({ ...all, [post.id]: next }))
      say(next ? t('pinned') : t('unpinned'))
    },
    publish: async (item) => {
      await of.send('PUT', `schedules/${item.id}/publish`, referer).catch(tell)
      dropItem(item.id)
      say(t('published'))
    },
    remove: async (item) => {
      await of.send('DELETE', `posts/${item.post!.id}`, referer).catch(tell)
      dropItem(item.id)
      say(t('deleted'))
    },
    edit: async (item, patch) => {
      await of.send('PUT', `posts/${item.post!.id}`, referer, editBody(item, patch)).catch(tell)
      changeItem(item.id, (i) => applyQueueEdit(i, patch))
      say(t('saved'))
    },
    openMedia: (items, index) => setViewer({ items, index }),
  }

  // ── Add to queue (Home's composer; OnlyFans then opens the post's day) ────
  const addToQueue = () => setCompose({ kind: 'schedule', at: scheduleOn(day) })
  const saveForLater = () => setCompose({ kind: 'later' })
  const posted = (_raw: Record<string, unknown>, when: When) => {
    setCompose(null)
    if (when.kind === 'schedule') {
      const d = dayInZone(when.at.toISOString(), timeZone)
      setView('queue')
      setDay(d)
      setMonth(monthOf(d))
      setCountsToken((n) => n + 1)
      reloadDay()
      setListToken((n) => n + 1)
      if (mobile) setSheet(true)
      say(t('composer.scheduled', { ns: 'ofHome' }))
    } else if (when.kind === 'later') {
      setView('post')
      setLaterToken((n) => n + 1)
      say(t('composer.savedLater', { ns: 'ofHome' }))
    } else {
      say(t('composer.posted', { ns: 'ofHome' }))
    }
  }
  const composer = compose && (
    <div className="ofq-scroll ofq-compose">
      <Composer of={of} accountId={accountId} openOn={null} when={compose} onClose={() => setCompose(null)} onPosted={posted} />
    </div>
  )
  const addButton = (label: string, onClick: () => void) =>
    canWrite && (
      <button type="button" className="lib-icon-btn" aria-label={label} title={label} onClick={onClick}>
        <FeatherIcon name="plus" size={21} />
      </button>
    )

  // ── Render ───────────────────────────────────────────────────────────────
  const monthTitle = dayLabel(firstOf(month), i18n.language, { month: 'long', year: 'numeric' })
  const pane = (onClose?: () => void) => (
    <DayPane
      day={day}
      today={today}
      items={dayItems}
      loading={dayLoading}
      failed={dayFailed}
      onRetry={reloadDay}
      onStep={(n) => pickDay(addDays(day, n))}
      onClose={onClose}
      actions={actions}
      mobile={mobile}
    />
  )

  const head = inQueue ? (
    <header className="lib-main-head ofq-head">
      <h1 className="lib-main-title ofq-title">{t('title')}</h1>
      <span className="lib-spacer" />
      <button type="button" className="lib-icon-btn" aria-label={t('savedMessages')} title={t('savedMessages')} onClick={() => setView('chat')}>
        <FeatherIcon name="message-square" size={19} />
      </button>
      <button type="button" className="lib-icon-btn" aria-label={t('savedPosts')} title={t('savedPosts')} onClick={() => setView('post')}>
        <FeatherIcon name="file-text" size={19} />
      </button>
      {addButton(t('add'), addToQueue)}
    </header>
  ) : (
    <header className="lib-main-head ofq-head">
      <button type="button" className="lib-icon-btn" aria-label={t('back')} onClick={() => setView('queue')}>
        <FeatherIcon name="arrow-left" size={19} />
      </button>
      <h1 className="lib-main-title ofq-title">{t(view === 'post' ? 'savedPosts' : 'savedMessages')}</h1>
      <span className="lib-spacer" />
      {view === 'post' && addButton(t('addLater'), saveForLater)}
    </header>
  )

  const toolbar = inQueue && (
    <>
      <div className="ofq-toolbar">
        <button type="button" className="lib-icon-btn" aria-label={t('prevMonth')} disabled={!canGoBack(month, today)} onClick={() => goMonth(-1)}>
          <FeatherIcon name="chevron-left" size={20} />
        </button>
        <button type="button" className="lib-icon-btn" aria-label={t('nextMonth')} onClick={() => goMonth(1)}>
          <FeatherIcon name="chevron-right" size={20} />
        </button>
        <h2 className="ofq-month">{monthTitle}</h2>
        <span className="lib-spacer" />
        <button
          type="button"
          className="lib-icon-btn"
          aria-label={layout === 'calendar' ? t('showList') : t('showCalendar')}
          title={layout === 'calendar' ? t('showList') : t('showCalendar')}
          onClick={() => setLayout(layout === 'calendar' ? 'list' : 'calendar')}
        >
          <FeatherIcon name={layout === 'calendar' ? 'list' : 'calendar'} size={18} />
        </button>
      </div>
      <div className="lib-chips ofq-chips" role="group" aria-label={t('types')}>
        {SCHEDULE_TYPES.map((ty) => {
          const on = types.includes(ty)
          return (
            <button key={ty} type="button" role="checkbox" aria-checked={on} className={`lib-chip ${on ? 'is-on' : ''}`} onClick={() => toggleType(ty)}>
              {t(`type.${ty}`)}
            </button>
          )
        })}
      </div>
    </>
  )

  const body = inQueue ? (
    layout === 'calendar' ? (
      <Calendar month={month} today={today} selected={mobile && !sheet ? null : day} counts={counts ?? {}} onPick={pickDay} />
    ) : (
      <QueueList
        items={list.items}
        loading={list.loading}
        failed={list.failed}
        hasMore={list.hasMore}
        onMore={list.loadMore}
        selected={mobile ? null : day}
        onPick={(d) => pickDay(d)}
        actions={actions}
        mobile={mobile}
      />
    )
  ) : (
    <LaterList
      kind={view}
      items={later.items}
      loading={later.loading}
      failed={later.failed}
      hasMore={later.hasMore}
      onMore={later.loadMore}
      actions={actions}
      mobile={mobile}
    />
  )

  const autoPanel = !inQueue && <AutoPublishPanel key={view} kind={view} of={of} canWrite={canWrite} say={say} />
  // The big "+" under the switch (posts only: the message composer isn't built).
  const addCard = canWrite && view === 'post' && (
    <button type="button" className="ofq-add-card" aria-label={t('addLater')} title={t('addLater')} onClick={saveForLater}>
      <FeatherIcon name="plus" size={44} />
    </button>
  )
  const mediaViewer = viewer && (
    <MediaViewer
      items={viewer.items}
      index={viewer.index}
      onIndex={(index) => setViewer((v) => v && { ...v, index })}
      onClose={() => setViewer(null)}
      hasMore={false}
      onLoadMore={async () => {}}
      mobile={mobile}
    />
  )

  if (!booted) {
    return (
      <div className={`lib ofp ofq ${mobile ? 'lib--m ofq--m' : ''}`}>
        <div className="ofq-loading">
          <LoadingBlock label={t('loading')} />
        </div>
        {mobile && mobileNav}
      </div>
    )
  }

  if (mobile) {
    return (
      <div className="lib lib--m ofp ofq ofq--m">
        {composer || (
          <>
            {head}
            {toolbar}
            <div className="ofq-scroll">
              {autoPanel}
              {body}
            </div>
          </>
        )}
        {mobileNav}
        {inQueue && sheet && !compose && (
          <>
            <div className="lib-sheet-backdrop" onClick={() => setSheet(false)} />
            <div className="ofq-sheet">
              <span className="lib-grip" aria-hidden="true" />
              {pane(() => setSheet(false))}
            </div>
          </>
        )}
        {mediaViewer}
        {toast}
      </div>
    )
  }

  return (
    <div className="lib ofp ofq">
      <main className="lib-main ofq-main">
        {composer || (
          <>
            {head}
            {toolbar}
            <div className="ofq-scroll">{body}</div>
          </>
        )}
      </main>
      <aside className="ofq-side">
        {inQueue ? (
          pane()
        ) : (
          <>
            {autoPanel}
            {addCard}
          </>
        )}
      </aside>
      {mediaViewer}
      {toast}
    </div>
  )
}
